import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiClient } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/ui/Logo';

const ProfileCompletion = () => {
    const navigate = useNavigate();
    const { user, updateUser } = useAuth();

    // Pre-filled profile fields from existing user data
    const [avatar, setAvatar] = useState<File | null>(null);
    const [avatarPreview, setAvatarPreview] = useState<string | null>(
        (user?.profile as any)?.avatar || (user as any)?.avatar || null
    );
    const [title, setTitle] = useState((user?.professional as any)?.title || '');
    const [bio, setBio] = useState((user?.profile as any)?.bio || '');
    const [availability, setAvailability] = useState((user?.professional as any)?.availability || '');
    const [hourlyRate, setHourlyRate] = useState(String((user?.professional as any)?.hourlyRate || ''));
    const [yearsOfExperience, setYearsOfExperience] = useState(String((user?.professional as any)?.yearsOfExperience || ''));
    const [portfolioUrl, setPortfolioUrl] = useState((user?.professional as any)?.portfolioUrl || '');
    const [resumeUrl, setResumeUrl] = useState((user?.professional as any)?.resumeUrl || '');
    const [whyJoin, setWhyJoin] = useState((user?.professional as any)?.onboarding?.whyJoin || '');
    const [goals, setGoals] = useState((user?.professional as any)?.onboarding?.goals || '');
    const [expertise, setExpertise] = useState((user?.professional as any)?.onboarding?.expertise || '');

    const [certificationMode, setCertificationMode] = useState<'formal' | 'self-taught' | 'experience' | 'apprenticeship'>('formal');
    const [learnedFrom, setLearnedFrom] = useState('');
    const [communityReputation, setCommunityReputation] = useState('');
    const [yearsHandsOn, setYearsHandsOn] = useState('');

    const [loading, setLoading] = useState(false);
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [error, setError] = useState('');

    // Sync if user loads after mount
    useEffect(() => {
        if (user) {
            const u = user as any;
            if (!avatarPreview) setAvatarPreview(u.avatar || u.profile?.avatar || null);
            if (!title) setTitle(u.professional?.title || '');
            if (!bio) setBio(u.profile?.bio || '');
            if (!availability) setAvailability(u.professional?.availability || '');
            if (!hourlyRate) setHourlyRate(String(u.professional?.hourlyRate || ''));
            if (!yearsOfExperience) setYearsOfExperience(String(u.professional?.yearsOfExperience || ''));
            if (!portfolioUrl) setPortfolioUrl(u.professional?.portfolioUrl || '');
            if (!resumeUrl) setResumeUrl(u.professional?.resumeUrl || '');
            if (!whyJoin) setWhyJoin(u.professional?.onboarding?.whyJoin || '');
            if (!goals) setGoals(u.professional?.onboarding?.goals || '');
            if (!expertise) setExpertise(u.professional?.onboarding?.expertise || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setAvatar(file);
            const reader = new FileReader();
            reader.onloadend = () => setAvatarPreview(reader.result as string);
            reader.readAsDataURL(file);
        }
    };

    const handleSkip = () => {
        navigate('/', { replace: true });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            if (avatar) {
                try {
                    await apiClient.uploadAvatar(avatar);
                } catch (uploadErr) {
                    console.error('Avatar upload failed:', uploadErr);
                }
            }

            const profileData: any = {};
            if (title) profileData.title = title;
            if (bio) profileData.bio = bio;
            if (availability) profileData.availability = availability;
            if (hourlyRate) profileData.hourlyRate = parseFloat(hourlyRate);
            if (yearsOfExperience) profileData.yearsOfExperience = parseInt(yearsOfExperience);
            if (portfolioUrl) profileData.portfolioUrl = portfolioUrl;
            if (resumeUrl) profileData.resumeUrl = resumeUrl;
            if (whyJoin) profileData.whyJoin = whyJoin;
            if (goals) profileData.goals = goals;
            if (expertise) profileData.expertise = expertise;
            profileData.certificationMode = certificationMode;

            if (certificationMode !== 'formal') {
                profileData.informalCredentials = {
                    learnedFrom: learnedFrom || undefined,
                    communityReputation: communityReputation || undefined,
                    yearsHandsOnExperience: yearsHandsOn ? parseInt(yearsHandsOn) : undefined,
                };
            }

            profileData.workModes = ['remote', 'freelance'];

            await updateUser(profileData);

            setSaveSuccess(true);
            setTimeout(() => navigate('/', { replace: true }), 800);
        } catch (err: any) {
            setError(err.message || 'Failed to save profile');
        } finally {
            setLoading(false);
        }
    };

    const isExistingUser = !!(title || bio || portfolioUrl);

    return (
        <div className="min-h-screen bg-premium-wood-dark font-sans text-white overflow-x-hidden relative">
            {/* Background */}
            <div
                className="absolute inset-0 z-0 opacity-30 pointer-events-none"
                style={{
                    backgroundImage: `url('https://images.unsplash.com/photo-1518455027359-f3f8164ba6bd?q=80&w=1600&auto=format&fit=crop')`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    filter: 'contrast(1.2) brightness(0.5)',
                }}
            />
            <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/70 via-premium-wood-dark/80 to-black/95 pointer-events-none" />

            {/* Gold border top accent */}
            <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-premium-gold to-transparent z-50" />

            {/* Header */}
            <header className="relative z-50 flex items-center justify-between px-6 py-4 border-b border-premium-gold/20 backdrop-blur-sm">
                <div className="flex items-center gap-3">
                    <Logo variant="sidebar" size="md" />
                </div>
                <button
                    onClick={handleSkip}
                    className="text-sm text-gray-400 hover:text-premium-gold-light transition-colors flex items-center gap-2 group"
                >
                    <span>Skip for now</span>
                    <span className="group-hover:translate-x-1 transition-transform">→</span>
                </button>
            </header>

            {/* Main Content */}
            <main className="relative z-10 container mx-auto px-4 py-8 max-w-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    {/* Step Header */}
                    <div className="text-center mb-8">
                        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-premium-gold/10 border border-premium-gold/30 text-premium-gold text-xs font-semibold tracking-widest uppercase mb-4">
                            {isExistingUser ? '✏️ Update Your Profile' : '🎉 Welcome to TechUnity'}
                        </div>
                        <h1 className="text-3xl font-bold mb-2 bg-gradient-to-r from-premium-gold-light via-white to-premium-gold-light bg-clip-text text-transparent">
                            {isExistingUser ? 'Review Your Professional Profile' : 'Set Up Your Professional Profile'}
                        </h1>
                        <p className="text-gray-400 text-sm max-w-md mx-auto">
                            {isExistingUser
                                ? 'Your existing details are pre-loaded. Update anything and save, or skip to continue.'
                                : 'This helps us match you with the right opportunities. Everything is optional — you can always update this later in Settings.'}
                        </p>
                    </div>

                    {/* Form Card */}
                    <div className="relative rounded-2xl border border-premium-gold/20 bg-gradient-to-br from-white/5 to-white/[0.02] backdrop-blur-xl shadow-2xl overflow-hidden">
                        {/* Glow corner */}
                        <div className="absolute top-0 right-0 w-48 h-48 bg-premium-gold/5 rounded-full blur-3xl pointer-events-none" />

                        <form onSubmit={handleSubmit} className="p-6 space-y-6">
                            {/* Avatar */}
                            <div className="flex items-center gap-6 pb-6 border-b border-white/10">
                                <div className="relative flex-shrink-0">
                                    <div className="w-20 h-20 rounded-full border-2 border-premium-gold/40 overflow-hidden bg-white/5 flex items-center justify-center">
                                        {avatarPreview ? (
                                            <img src={avatarPreview} alt="Avatar preview" className="w-full h-full object-cover" />
                                        ) : (
                                            <span className="text-2xl font-bold text-premium-gold/60">
                                                {user?.firstName?.[0]?.toUpperCase() || '?'}
                                            </span>
                                        )}
                                    </div>
                                    <label className="absolute -bottom-1 -right-1 w-7 h-7 bg-premium-gold rounded-full flex items-center justify-center cursor-pointer hover:bg-premium-gold-light transition-colors shadow-lg">
                                        <svg className="w-3.5 h-3.5 text-premium-wood-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <input type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                                    </label>
                                </div>
                                <div>
                                    <p className="font-semibold text-white">{user?.firstName} {user?.lastName}</p>
                                    <p className="text-xs text-gray-500">{user?.email}</p>
                                    <p className="text-xs text-premium-gold/60 mt-1">Click the camera icon to upload a photo</p>
                                </div>
                            </div>

                            {/* Professional Title + Availability */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Professional Title</label>
                                    <input
                                        type="text"
                                        value={title}
                                        onChange={(e) => setTitle(e.target.value)}
                                        placeholder="e.g., Full Stack Developer"
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Availability</label>
                                    <select
                                        value={availability}
                                        onChange={(e) => setAvailability(e.target.value)}
                                        className="w-full px-3 py-2.5 bg-[#1a1410] border border-white/10 rounded-lg text-white focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                    >
                                        <option value="">Select availability</option>
                                        <option value="Full-time">Full Time</option>
                                        <option value="Part-time">Part Time</option>
                                        <option value="Contract">Contract / Freelance</option>
                                        <option value="Unavailable">Not Available Right Now</option>
                                    </select>
                                </div>
                            </div>

                            {/* Hourly Rate + Experience */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Hourly Rate (USD)</label>
                                    <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">$</span>
                                        <input
                                            type="number"
                                            value={hourlyRate}
                                            onChange={(e) => setHourlyRate(e.target.value)}
                                            placeholder="25"
                                            min="1"
                                            className="w-full pl-7 pr-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Years of Experience</label>
                                    <input
                                        type="number"
                                        value={yearsOfExperience}
                                        onChange={(e) => setYearsOfExperience(e.target.value)}
                                        placeholder="3"
                                        min="0"
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                    />
                                </div>
                            </div>

                            {/* Bio */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Bio</label>
                                <textarea
                                    value={bio}
                                    onChange={(e) => setBio(e.target.value)}
                                    placeholder="Tell the TechUnity community about yourself..."
                                    rows={3}
                                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm resize-none"
                                />
                            </div>

                            {/* How did you learn your skills? */}
                            <div className="p-4 bg-premium-gold/5 rounded-xl border border-premium-gold/15">
                                <label className="block text-xs font-semibold text-premium-gold mb-3 flex items-center gap-2 uppercase tracking-wider">
                                    <span>🎓</span> How did you learn your skills?
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                    {[
                                        { id: 'formal', label: 'Formal Education', icon: '🏛️' },
                                        { id: 'self-taught', label: 'Self-Taught', icon: '📖' },
                                        { id: 'experience', label: 'Hands-on Work', icon: '🛠️' },
                                        { id: 'apprenticeship', label: 'Apprenticeship', icon: '🤝' },
                                    ].map(mode => (
                                        <button
                                            key={mode.id}
                                            type="button"
                                            onClick={() => setCertificationMode(mode.id as any)}
                                            className={`p-3 rounded-lg border text-center transition-all ${certificationMode === mode.id
                                                    ? 'bg-premium-gold/20 border-premium-gold text-premium-gold'
                                                    : 'bg-white/5 border-white/10 text-white/40 hover:border-white/20 hover:text-white/60'
                                                }`}
                                        >
                                            <div className="text-2xl mb-1">{mode.icon}</div>
                                            <div className="text-[10px] font-bold uppercase tracking-wider leading-tight">{mode.label}</div>
                                        </button>
                                    ))}
                                </div>

                                <AnimatePresence>
                                    {certificationMode !== 'formal' && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            className="mt-4 pt-4 border-t border-premium-gold/10 grid sm:grid-cols-2 gap-3 overflow-hidden"
                                        >
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Who / where taught you?</label>
                                                <input
                                                    type="text"
                                                    value={learnedFrom}
                                                    onChange={(e) => setLearnedFrom(e.target.value)}
                                                    placeholder="e.g., Father's workshop, YouTube"
                                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/40 text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Community reputation (optional)</label>
                                                <input
                                                    type="text"
                                                    value={communityReputation}
                                                    onChange={(e) => setCommunityReputation(e.target.value)}
                                                    placeholder="e.g., Known as best welder in town"
                                                    className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/40 text-sm"
                                                />
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>

                            {/* Portfolio + Resume */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Portfolio URL</label>
                                    <input
                                        type="url"
                                        value={portfolioUrl}
                                        onChange={(e) => setPortfolioUrl(e.target.value)}
                                        placeholder="https://yourportfolio.com"
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Resume URL</label>
                                    <input
                                        type="url"
                                        value={resumeUrl}
                                        onChange={(e) => setResumeUrl(e.target.value)}
                                        placeholder="https://drive.google.com/..."
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                    />
                                </div>
                            </div>

                            {/* Areas of Expertise */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Areas of Expertise</label>
                                <input
                                    type="text"
                                    value={expertise}
                                    onChange={(e) => setExpertise(e.target.value)}
                                    placeholder="e.g., React, Node.js, UI/UX, Welding, Accounting"
                                    className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm"
                                />
                            </div>

                            {/* Why join + Goals */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Why are you joining?</label>
                                    <textarea
                                        value={whyJoin}
                                        onChange={(e) => setWhyJoin(e.target.value)}
                                        placeholder="What brings you to TechUnity?"
                                        rows={2}
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm resize-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-400 mb-1.5 uppercase tracking-wider">Your Goals</label>
                                    <textarea
                                        value={goals}
                                        onChange={(e) => setGoals(e.target.value)}
                                        placeholder="What do you hope to achieve?"
                                        rows={2}
                                        className="w-full px-3 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-premium-gold/50 focus:ring-1 focus:ring-premium-gold/30 transition-all text-sm resize-none"
                                    />
                                </div>
                            </div>

                            {/* Error */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg"
                                    >
                                        <p className="text-sm text-red-400">{error}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={handleSkip}
                                    className="flex-1 py-3 border border-white/15 text-gray-400 rounded-xl hover:bg-white/5 hover:text-white hover:border-white/25 transition-all text-sm font-medium"
                                >
                                    ← Skip for Now
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading || saveSuccess}
                                    className="flex-1 py-3 bg-gradient-to-r from-premium-gold-dark via-premium-gold to-premium-gold-light text-premium-wood-dark font-bold rounded-xl hover:shadow-[0_0_30px_rgba(212,175,55,0.5)] transition-all disabled:opacity-60 disabled:cursor-not-allowed text-sm flex items-center justify-center gap-2"
                                >
                                    {saveSuccess ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                            Saved!
                                        </>
                                    ) : loading ? (
                                        <>
                                            <div className="w-4 h-4 border-2 border-premium-wood-dark/40 border-t-premium-wood-dark rounded-full animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        'Save Changes & Continue →'
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>

                    {/* You can always update this later note */}
                    <p className="text-center text-xs text-gray-600 mt-4">
                        All fields are optional. You can update your profile anytime in{' '}
                        <button onClick={handleSkip} className="text-premium-gold/60 hover:text-premium-gold underline transition-colors">Settings</button>.
                    </p>
                </motion.div>
            </main>
        </div>
    );
};

export default ProfileCompletion;
